<template>
  <loadable
    :loading="!!loading"
    :description="loading"
    :show-progress="isDownloading || isDeleting"
    :progress="isDownloading ? downloadProgress : deleteProgress"
    only-hide
  >
    <model-crud
      :model="model"
      :fields="fields"
      :actions="(bulk && actions) ? bulkActions : []"
      :allItemsActions="$auth.hasRole('COMPANY_ADMIN') && $store.auth.user.company && $store.auth.user.company.allowRecordingDeletion ? allItemsActions : []"
      :include="include"
      :extended="true"
      :paginating="paginating"
      :editable="false"
      :deletable="false"
      :exportable="exportable"
      :bulk="bulk && !recordingsConfirmations"
      :filtering="filtering"
      default-order="callDate"
      :default-order-asc="false"
      :perPage="perPage"
      ref="crud"
    >
      <template slot="tags" slot-scope="row">
        <div @click.stop="tag(row.item)">
          <v-chip label
            v-for="tag in row.item.tags"
            :key="tag._self"
            class="light-blue darken-1 white--text mx-1 tag-chip"
            :style="tag.color ? `background-color: ${tag.color} !important; border: 0 !important;` : ''"
          >
            {{tag.tagName}}
          </v-chip>
          <v-btn icon class="ma-0" v-if="row.item.tags.length === 0"><v-icon>add</v-icon></v-btn>
        </div>
      </template>
      <template slot="actions" slot-scope="row">
        <div class="text-xs-right no-wrap" v-if="isPlayable(row.item)">
          <v-icon v-if="row.item.encrypted" v-tooltip="{ content: $t('recordings.recordingIsEncrypted') }" class="recording-lock">lock</v-icon>
          <template v-if="!recordingsConfirmations">
            <v-btn icon class="ma-0" @click="play(row.item)" v-tooltip="{ content: $t('recordings.play') }"><v-icon>play_arrow</v-icon></v-btn>
            <v-btn icon class="ma-0" @click="download(row.item)" v-tooltip="{ content: $t('recordings.download') }"><v-icon>get_app</v-icon></v-btn>
            <v-btn icon class="ma-0" @click="downloadMetadata(row.item)" v-tooltip="{ content: $t('recordings.downloadMetadata') }"><v-icon>attachment</v-icon></v-btn>
            <v-btn icon class="ma-0" v-if="$store.auth.user.company && $store.auth.user.company.allowRecordingDeletion" @click="archive(row.item)" v-tooltip="{ content: $t('recordings.delete') }"><v-icon>delete</v-icon></v-btn>
          </template>
          <template v-else>
            <v-btn icon class="ma-0" @click="request(row.item, 'PLAY')" v-tooltip="{ content: $t('recordings.requestPlay') }"><v-icon>play_arrow</v-icon></v-btn>
            <v-btn icon class="ma-0" @click="request(row.item, 'DOWNLOAD')" v-tooltip="{ content: $t('recordings.requestDownload') }"><v-icon>get_app</v-icon></v-btn>
            <v-btn icon class="ma-0" v-if="$store.auth.user.company && $store.auth.user.company.allowRecordingDeletion" @click="request(row.item, 'DELETE')" v-tooltip="{ content: $t('recordings.requestDelete') }"><v-icon>delete</v-icon></v-btn>
          </template>
        </div>
        <div class="text-xs-right pr-2" v-if="!isPlayable(row.item) && !row.item.archived">
          <v-icon v-tooltip="{ content: formatReason(row.item) }" class="recording-reason">info</v-icon>
        </div>
        <div class="text-xs-right" v-if="row.item.archived">
          <v-icon v-tooltip="{ content: $t('recordings.archived') }" class="recording-lock">cloud</v-icon>
        </div>
      </template>
    </model-crud>
    <player ref="player" :record="playerRecord" @closed="playerClosed" />
    <tagger ref="tagger" :record="taggerRecord" @save="saveTags" />

    <v-dialog v-model="showRequest" persistent width="350px" scrollable>
      <v-card>
        <v-card-title class="headline">{{ $t('recordings.actionRequest') }}</v-card-title>
        <v-card-text>
          {{ $t('recordings.requestQuestion') }}
        </v-card-text>
        <v-card-actions>
          <v-btn class="elevation-0" success @click="doRequest">{{ $t('yes') }}</v-btn>
          <v-spacer />
          <v-btn class="elevation-0" error @click="showRequest = false">{{ $t('no') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </loadable>
</template>

<script>
import ModelCrud from '@/components/ModelCrud'
import Player from '@/components/Player'
import Tagger from '@/components/Tagger'

import Record from '@/models/Record'
import Tag from '@/models/Tag'
import Line from '@/models/Line'
import FileSystem from '@/models/FileSystem'

import Downloader from '@/utils/Downloader'

import RecordingRequest from '@/models/RecordingRequest'

export default {
  props: {
    paginating: Boolean,
    filtering: Boolean,
    exportable: Boolean,
    bulk: Boolean,
    actions: Boolean,
    perPage: {
      type: Number,
      default: 19
    }
  },
  data () {
    return {
      model: Record,
      include: [],
      fields: {
        target: { label: this.$t('headers.target'), type: 'reference', model: Line },
        callDate: { label: this.$t('headers.callDate'), type: 'date' },
        source: { label: this.$t('headers.source') },
        destination: { label: this.$t('headers.destination') },
        duration: { label: this.$t('headers.duration'), type: 'interval' },
        direction: { label: this.$t('headers.direction'), type: 'enum', values: Record.enums.direction },
        state: { label: this.$t('headers.state'), type: 'enum', values: Record.enums.state },
        tags: { label: this.$t('headers.tags'), type: 'references', model: Tag, display: 'tagName' },
        actions: { label: '', editable: false, filterable: false, exportable: false }
      },
      bulkActions: [
        {
          single: false,
          multi: true,
          multiLabel: this.$t('recordings.downloadSelected'),
          icon: 'get_app',
          action: (items) => {
            this.downloadItems(items)
          }
        },
        {
          single: false,
          multi: true,
          multiLabel: this.$t('recordings.deleteSelected'),
          icon: 'delete',
          color: 'error',
          action: (items) => {
            this.deleteItems(items)
          }
        }
      ],
      allItemsActions: [
        {
          multiLabel: this.$t('recordings.deleteFiltered'),
          icon: 'delete',
          color: 'error',
          action: (items) => {
            this.deleteItems(items, false)
          }
        }
      ],
      playerRecord: null,
      taggerRecord: null,
      loading: null,
      showRequest: false,
      requestOperation: null,
      requestItem: null,
      isDownloading: false,
      downloadProgress: null,
      isDeleting: false,
      deleteProgress: null
    }
  },
  computed: {
    recordingsConfirmations () {
      return !this.$auth.hasRole('COMPANY_ADMIN') && this.$store.auth.user.company.recordingsConfirmations
    }
  },
  methods: {
    play (item) {
      this.playerRecord = item
      this.$refs.player.show()
    },

    async deleteItems (items, onlySelected = true) {
      const userPrompt = prompt(
          onlySelected
            ? this.$t('recordings.deleteSelectedPrompt')
            : this.$t('recordings.deleteFilteredPrompt', { number: items.length }),
          ''
        ).toLowerCase()

      if (
        userPrompt !== (onlySelected
          ? 'delete selected'
          : 'delete filtered')
      ) {
        return
      }

      this.loading = this.$t('recordings.deleting')
      this.isDeleting = true
      this.deleteProgress = 0

      let index = 0
      while (index < items.length) {
        this.deleteProgress = Math.round((index / items.length) * 100)
        const batchSize = Math.min(items.length - index, 50)
        if (batchSize > 0) {
          try {
            await FileSystem.archiveRecordings(
            items
              .slice(index, index + batchSize)
              .map(item => item.id)
          )
          } catch (e) {
            console.error('Failed to delete recordings', e)
          }

          index += batchSize
        } else {
          break
        }
      }

      this.loading = null
      this.isDeleting = false
      this.deleteProgress = null

      this.$refs.crud.refresh()
    },

    download (item) {
      // Encrypted items should be decrypted first
      if (item.encrypted) {
        this.playerRecord = item
        this.$refs.player.show(true)
      } else {
        // Plain recordings use same logic as batch download, just with different output name
        this.downloadItems([item], Record.getBasename(item) + '.zip')
      }
    },

    /**
     * Downloads recordings.
     * NOTE: Encrypted recordings are NOT decrypted when downloaded in batch.
     *
     * @param {Object[]} items recordings to download
     * @param {string} filename filename of resulting archive
     */
    downloadItems (items, filename) {
      this.loading = this.$t('recordings.downloading')

      this.isDownloading = true
      this.downloadProgress = 0

      Downloader
        .downloadRecordings(
          items,
          filename,
          (downloaded, total) => {
            this.downloadProgress = Math.floor((downloaded / total) * 100)
          }
        )
        .then(result => {
          this.isDownloading = false
          this.loading = null
        }).catch(() => {
          this.isDownloading = false
          this.loading = null
        })
    },

    downloadMetadata (item) {
      Record.getMetadata(item)
        .then(data => {
          let blob = new Blob(
            [ data ],
            { type: 'text/plain' }
          )

          Downloader.download(
            Record.getBasename(item) + '.txt',
            blob
          )
        })
    },
    tag (item) {
      this.taggerRecord = item
      this.$refs.tagger.show()
    },
    saveTags (tags, item) {
      item.tags = tags
      Record.saveTags(item)
    },
    colorize (tag) {
      return '#' + this.intToARGB(this.hashCode(tag._self))
    },
    hashCode (str) {
      var hash = 0
      for (var i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
      }
      return hash
    },
    intToARGB (i) {
      function normalize (c) {
        return Math.round(128 + (c / 255) * 127)
      }

      return normalize((i >> 16) & 0xFF).toString(16) +
        normalize((i >> 8) & 0xFF).toString(16) +
        normalize((i) & 0xFF).toString(16)
    },
    playerClosed () {
      this.playerRecord = null
    },
    isPlayable (item) {
      return Record.isPlayable(item)
    },
    archive (item) {
      if ((prompt(this.$t('recordings.deletePrompt')) || '').toLowerCase() !== 'delete') return

      return FileSystem
        .archiveRecording(item.id)
        .then(() => {
          item.archived = true
        })
    },
    request (item, operation) {
      this.showRequest = true
      this.requestOperation = operation
      this.requestItem = item
    },
    doRequest () {
      RecordingRequest.create({
        recording: this.requestItem,
        operation: this.requestOperation,
        user: this.$store.auth.user,
        created: new Date()
      })
      this.showRequest = false
    },
    formatReason (item) {
      if (!item.reason) return this.$t(Record.enums.reason.UNKNOWN)

      let data
      try {
        data = JSON.parse(data)
      } catch (e) {
        return this.$t(Record.enums.reason.UNKNOWN)
      }

      return data.reasons.map(item => {
        return this.$t(Record.enums.reason[item.reason], item)
      }).join('<br />')
    }
  },
  components: {
    ModelCrud,
    Player,
    Tagger
  }
}
</script>

<style>
.recording-lock.icon {
  color: rgba(0, 0, 0, 0.2);
}

.no-wrap {
  white-space: nowrap;
}

.tag-chip {
  text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);
}

</style>



// WEBPACK FOOTER //
// Records.vue?12884910